import styles from './Sparkline.module.scss';

/** Longer than this, two readings are not one measured stretch. Hourly data steps by one hour. */
const MAX_JOIN_MS = 90 * 60 * 1000;

export interface SparklinePoint {
    at: number;
    value: number | null;
}

interface SparklineProps {
    /** Readings in order. A null breaks the line. A larger value is drawn lower, the way depth is. */
    values?: Array<number | null>;
    /**
     * Readings placed by time. A null, or a step longer than an hour and a half, breaks the
     * line so a missing stretch is not drawn as a measurement.
     */
    points?: SparklinePoint[];
    /** Time span `points` are placed in. Defaults to the first and last timestamp. */
    from?: number;
    to?: number;
    className?: string;
}

interface Sample {
    x: number;
    y: number;
    at?: number;
}

/**
 * A small line of a series, painted with `currentColor` so the card decides the colour.
 */
export default function Sparkline(props: SparklineProps) {
    const { values, points, from, to, className } = props;
    const samples = points ? timedSamples(points, from, to) : indexedSamples(values ?? []);
    const segments = splitSegments(samples);
    const lines = segments.filter((segment) => segment.length >= 2);
    // A lone timed reading still shows. An index-only series needs two readings to be a line.
    const dots = points ? segments.filter((segment) => segment.length === 1) : [];
    if (lines.length === 0 && dots.length === 0) return null;

    return (
        <svg
            className={className ? `${styles.sparkline} ${className}` : styles.sparkline}
            viewBox="0 0 100 24"
            aria-hidden="true">
            {lines.map((segment) => {
                const drawn = toPoints(segment);
                return <polyline key={drawn} points={drawn} />;
            })}
            {dots.map((segment) => {
                const dot = segment[0];
                return <circle key={`${dot.x},${dot.y}`} cx={dot.x} cy={dot.y} r={1.5} />;
            })}
        </svg>
    );
}

function indexedSamples(values: Array<number | null>): Array<Sample | null> {
    const numbers = values.filter((value): value is number => value != null);
    if (numbers.length === 0) return [];
    const yOf = yScale(numbers);
    const last = values.length - 1;
    return values.map((value, index) => {
        if (value == null) return null;
        const x = last === 0 ? 0 : (index / last) * 100;
        return { x, y: yOf(value) };
    });
}

function timedSamples(
    points: SparklinePoint[],
    from: number | undefined,
    to: number | undefined
): Array<Sample | null> {
    const numbers = points.flatMap((point) => (point.value == null ? [] : [point.value]));
    if (numbers.length === 0) return [];
    const yOf = yScale(numbers);
    const start = from ?? points[0]?.at ?? 0;
    const end = to ?? points[points.length - 1]?.at ?? start;
    const span = end - start || 1;
    return points.map((point) => {
        if (point.value == null) return null;
        const x = Math.min(100, Math.max(0, ((point.at - start) / span) * 100));
        return { x, y: yOf(point.value), at: point.at };
    });
}

function yScale(values: number[]): (value: number) => number {
    const low = Math.min(...values);
    const high = Math.max(...values);
    const span = high - low || 1;
    return (value) => 2 + ((value - low) / span) * 20;
}

/** A null or a long time step starts a new stretch. */
function splitSegments(samples: Array<Sample | null>): Sample[][] {
    const segments: Sample[][] = [];
    let current: Sample[] = [];
    for (const sample of samples) {
        const previous = current[current.length - 1];
        const jumped =
            sample != null &&
            previous?.at !== undefined &&
            sample.at !== undefined &&
            sample.at - previous.at > MAX_JOIN_MS;
        if (sample == null || jumped) {
            if (current.length > 0) segments.push(current);
            current = sample == null ? [] : [sample];
            continue;
        }
        current.push(sample);
    }
    if (current.length > 0) segments.push(current);
    return segments;
}

function toPoints(segment: Sample[]): string {
    return segment.map((sample) => `${sample.x},${sample.y}`).join(' ');
}
